import type { Issue } from "@qualtio-workhub/contracts";
import type { TranslationKey } from "@qualtio-workhub/i18n";

export const statusOptions = [
  "triage",
  "backlog",
  "todo",
  "in_progress",
  "in_review",
  "blocked",
  "done",
  "canceled",
] as const;

export const priorityOptions = ["none", "low", "medium", "high", "urgent"] as const;

export const typeOptions = [
  "task",
  "bug",
  "feature",
  "research",
  "spike",
  "client_request",
  "internal_ops",
  "qa",
  "devops",
  "agent_task",
] as const;

export type IssueStatus = (typeof statusOptions)[number];
export type IssuePriority = (typeof priorityOptions)[number];
export type IssueType = (typeof typeOptions)[number];

export const statusLabelKeys: Record<IssueStatus, TranslationKey> = {
  backlog: "issue.status.backlog",
  blocked: "issue.status.blocked",
  canceled: "issue.status.canceled",
  done: "issue.status.done",
  in_progress: "issue.status.inProgress",
  in_review: "issue.status.inReview",
  todo: "issue.status.todo",
  triage: "issue.status.triage",
};

export const priorityLabelKeys: Record<IssuePriority, TranslationKey> = {
  high: "issue.priority.high",
  low: "issue.priority.low",
  medium: "issue.priority.medium",
  none: "issue.priority.none",
  urgent: "issue.priority.urgent",
};

export const typeLabelKeys: Record<IssueType, TranslationKey> = {
  agent_task: "issue.type.agentTask",
  bug: "issue.type.bug",
  client_request: "issue.type.clientRequest",
  devops: "issue.type.devops",
  feature: "issue.type.feature",
  internal_ops: "issue.type.internalOps",
  qa: "issue.type.qa",
  research: "issue.type.research",
  spike: "issue.type.spike",
  task: "issue.type.task",
};

export const statusTone: Record<
  IssueStatus,
  "neutral" | "accent" | "success" | "warning" | "danger"
> = {
  backlog: "neutral",
  blocked: "danger",
  canceled: "neutral",
  done: "success",
  in_progress: "accent",
  in_review: "warning",
  todo: "neutral",
  triage: "warning",
};

export function openIssueCount(issues: Issue[]): number {
  return issues.filter((issue) => !["done", "canceled"].includes(issue.status)).length;
}

export function groupIssuesByStatus(issues: Issue[]): Record<IssueStatus, Issue[]> {
  return Object.fromEntries(
    statusOptions.map((status) => [status, issues.filter((issue) => issue.status === status)]),
  ) as Record<IssueStatus, Issue[]>;
}

export function applyIssueStatusChange(
  issues: Issue[],
  issueKey: string,
  status: IssueStatus,
): Issue[] {
  return issues.map((issue) => (issue.key === issueKey ? { ...issue, status } : issue));
}

export function statusChangePayload(status: IssueStatus) {
  return {
    status,
    blockedReason: status === "blocked" ? "Blocked from WorkHub" : undefined,
    doneSummary: status === "done" ? "Completed from WorkHub" : undefined,
  };
}

export function sortByUpdatedAt(issues: Issue[]): Issue[] {
  return [...issues].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );
}

export function findRecordNameById<T extends { id: string; name: string }>(
  items: readonly T[] | null | undefined,
  id: string | null | undefined,
): string | null {
  if (!id) {
    return null;
  }

  return items?.find((item) => item.id === id)?.name ?? null;
}

export function findRecordNamesByIds<T extends { id: string; name: string }>(
  items: readonly T[] | null | undefined,
  ids: readonly string[],
): string[] {
  if (items === null || items === undefined || ids.length === 0) {
    return [];
  }

  const namesById = new Map(items.map((item) => [item.id, item.name]));
  return ids.flatMap((id) => {
    const name = namesById.get(id);
    return name === undefined ? [] : [name];
  });
}

export function formatIssueReference(
  issue: Pick<Issue, "key" | "title"> | null | undefined,
): string | null {
  return issue ? `${issue.key} · ${issue.title}` : null;
}
